import React, { ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import './DataTable.scss';

export interface Column<T = any> {
  id: string;
  label: string | ReactNode;
  align?: 'left' | 'center' | 'right';
  minWidth?: number;
  width?: number;
  className?: string;
  render?: (row: T, index: number) => ReactNode;
  sortable?: boolean;
}

export interface DataTableProps<T = any> {
  columns: Column<T>[];
  data: T[];
  className?: string;
  stickyHeader?: boolean;
  size?: 'small' | 'medium';
  maxHeight?: string | number;
  emptyMessage?: string;
  rowKey?: (row: T, index: number) => string | number;
  onRowClick?: (row: T, index: number) => void;
  renderEmpty?: () => ReactNode;
  containerComponent?: React.ElementType;
  headerClassName?: string;
}

export function DataTable<T = any>({
  columns,
  data,
  className = '',
  stickyHeader = false,
  size = 'medium',
  maxHeight,
  emptyMessage = 'No data available',
  rowKey,
  onRowClick,
  renderEmpty,
  containerComponent,
  headerClassName = '',
}: DataTableProps<T>) {
  const getRowKey = (row: T, index: number): string | number => {
    if (rowKey) {
      return rowKey(row, index);
    }
    return (row as any).id ?? index;
  };

  const getCellValue = (column: Column<T>, row: T, index: number): ReactNode => {
    if (column.render) {
      return column.render(row, index);
    }
    return (row as any)[column.id];
  };

  const tableContent = (
    <Table size={size} stickyHeader={stickyHeader}>
      <TableHead className={`data-table-head ${headerClassName}`}>
        <TableRow>
          {columns.map((column) => (
            <TableCell
              key={column.id}
              align={column.align || 'left'}
              style={{
                minWidth: column.minWidth,
                width: column.width,
              }}
              className={column.className}
            >
              {column.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {data.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columns.length} align="center" className="empty-state">
              {renderEmpty ? renderEmpty() : emptyMessage}
            </TableCell>
          </TableRow>
        ) : (
          data.map((row, index) => (
            <TableRow
              key={getRowKey(row, index)}
              hover={!!onRowClick}
              onClick={() => onRowClick?.(row, index)}
              style={{ cursor: onRowClick ? 'pointer' : 'default' }}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  className={column.className}
                >
                  {getCellValue(column, row, index)}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  return (
    <TableContainer
      component={containerComponent || Paper}
      className={`data-table-container ${className}`}
      style={{ maxHeight }}
    >
      {tableContent}
    </TableContainer>
  );
}
